/**
 * F606ProxyDashboard — "606 فتنس" main data dashboard.
 *
 * Talks to the admin panel's own `/api/f606/*` proxy which injects the
 * `X-Integration-Key` secret server-side. Styled to match the rest of the
 * admin panel (DashboardLayout + glass cards identical to other sections).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Users, ShoppingBag, ClipboardList, Wifi, WifiOff, Package, CreditCard,
  Trophy, Building2, Megaphone, Bot, Star, Heart, Apple, Scale,
  User as UserIcon, RefreshCw, Zap, SlidersHorizontal,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { getAuthHeaders } from '../../utils/authHeaders';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

const API = process.env.REACT_APP_BACKEND_URL;

const F606_SECTIONS = [
  { id: 'users',          icon: Users,         color: '#38bdf8', ar: 'الحسابات',       en: 'Users',         stat: 'total_users',          code: 'F606-01' },
  { id: 'trainers',       icon: Trophy,        color: '#a855f7', ar: 'المدربون',       en: 'Trainers',      stat: 'total_trainers',       code: 'F606-02' },
  { id: 'trainees',       icon: UserIcon,      color: '#f59e0b', ar: 'المتدربون',      en: 'Trainees',      stat: 'total_trainees',       code: 'F606-03' },
  { id: 'programs',       icon: ClipboardList, color: '#22c55e', ar: 'البرامج',        en: 'Programs',      stat: 'total_programs',       code: 'F606-04' },
  { id: 'products',       icon: Package,       color: '#d4af37', ar: 'المتجر',         en: 'Products',      stat: 'total_products',       code: 'F606-05' },
  { id: 'orders',         icon: ShoppingBag,   color: '#f97316', ar: 'الطلبات',        en: 'Orders',        stat: 'total_orders',         code: 'F606-06' },
  { id: 'subscriptions',  icon: CreditCard,    color: '#06b6d4', ar: 'الاشتراكات',     en: 'Subscriptions', stat: 'active_subscriptions', code: 'F606-07' },
  { id: 'challenges',     icon: Trophy,        color: '#eab308', ar: 'التحديات',       en: 'Challenges',    stat: 'total_challenges',     code: 'F606-08' },
  { id: 'clubs',          icon: Building2,     color: '#8b5cf6', ar: 'الأندية',        en: 'Clubs',         stat: 'total_clubs',          code: 'F606-09' },
  { id: 'announcements',  icon: Megaphone,     color: '#ec4899', ar: 'الإعلانات',      en: 'Announcements', stat: 'total_announcements',  code: 'F606-10' },
  { id: 'coach-messages', icon: Bot,           color: '#0ea5e9', ar: 'رسائل الكوتش',  en: 'Coach msgs',    stat: 'total_coach_messages', code: 'F606-11' },
  { id: 'ratings',        icon: Star,          color: '#facc15', ar: 'التقييمات',      en: 'Ratings',       stat: 'total_ratings',        code: 'F606-12' },
  { id: 'follows',        icon: Heart,         color: '#f43f5e', ar: 'المتابعات',      en: 'Follows',       stat: 'total_follows',        code: 'F606-13' },
  { id: 'food-analyses',  icon: Apple,         color: '#84cc16', ar: 'تحليل الغذاء',  en: 'Food analyses', stat: 'total_food_analyses',  code: 'F606-14' },
  { id: 'weight-logs',    icon: Scale,         color: '#a78bfa', ar: 'سجلات الوزن',    en: 'Weight logs',   stat: 'total_weight_logs',    code: 'F606-15' },
  { id: 'webhook-logs',   icon: Zap,           color: '#f43f5e', ar: 'سجل Webhooks',   en: 'Webhook logs',  stat: 'total_webhook_logs',   code: 'F606-16' },
];

function SectionCard({ icon: Icon, color, name, code, statVal, onClick, testId, subtitle }) {
  return (
    <motion.button
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:bg-white/[0.04] overflow-hidden"
      data-testid={testId}
    >
      {/* Screen Code Badge */}
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md" style={{ backgroundColor: `${color}15` }}>
        <span className="text-[10px] font-mono" style={{ color }}>{code}</span>
      </div>
      {/* Hover Glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
        style={{ background: `radial-gradient(circle at center, ${color}10 0%, transparent 70%)` }}
      />
      {/* Border Glow on Hover */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ boxShadow: `inset 0 0 0 1px ${color}60, 0 8px 32px ${color}20` }}
      />
      {/* Glass Shine */}
      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <div className="relative">
        <div
          className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center group-hover:scale-110 transition-all duration-300 border"
          style={{ backgroundColor: `${color}15`, borderColor: `${color}30` }}
        >
          <Icon className="h-8 w-8" style={{ color }} strokeWidth={1.5} />
        </div>
        <h3 className="text-[#F5F5F5] font-bold text-sm transition-colors">{name}</h3>
        {subtitle && <p className="text-[#71717A] text-[11px] mt-1">{subtitle}</p>}
        {statVal != null && (
          <p className="text-[#71717A] text-xs mt-1 font-mono" dir="ltr">
            {Number(statVal).toLocaleString()}
          </p>
        )}
      </div>
    </motion.button>
  );
}

export default function F606ProxyDashboard() {
  const navigate = useNavigate();
  const { visiblePages, canViewRoute } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [hRes, sRes] = await Promise.all([
        axios.get(`${API}/api/f606/health`).catch((e) => ({ data: { error: e.message } })),
        axios.get(`${API}/api/f606/stats/overview`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setHealth(hRes.data);
      setStats(sRes.data?.data || sRes.data || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const connected = health?.reachable === true;

  return (
    <DashboardLayout
      title="606 فتنس"
      titleEn="606 FITNESS"
      subtitle="لوحة البيانات الكاملة — متصلة مباشرة بتطبيق 606 FITNESS"
      subtitleEn="Full data dashboard — wired directly to the 606 FITNESS app"
      screenCode="F606-000"
      backPath="/dashboard"
      onBack={() => navigate('/dashboard')}
      breadcrumb={[
        { labelAr: '606 فتنس', labelEn: '606 FITNESS' },
      ]}
    >
      <section dir={isAr ? 'rtl' : 'ltr'} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Health chip + refresh */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold ${
              connected
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                : health === null
                  ? 'bg-white/5 border-white/10 text-white/40'
                  : 'bg-red-500/15 border-red-500/40 text-red-300'
            }`}
            data-testid="f606-health-chip"
          >
            {connected ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            {health === null
              ? (isAr ? 'جارٍ الفحص...' : 'Checking...')
              : connected
                ? (isAr ? 'متصل بالتطبيق' : 'App connected')
                : (isAr ? 'غير متصل بالتطبيق' : 'App unreachable')}
          </span>
          <button
            onClick={refresh}
            disabled={loading}
            className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white/70 disabled:opacity-50 transition"
            data-testid="f606-refresh"
            title={isAr ? 'تحديث' : 'Refresh'}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Aggregate stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: isAr ? 'إجمالي المستخدمين' : 'Total users', value: stats.total_users, icon: Users, color: '#38bdf8', testId: 'f606-stat-users' },
              { label: isAr ? 'المنتجات' : 'Products', value: stats.total_products, icon: Package, color: '#d4af37', testId: 'f606-stat-products' },
              { label: isAr ? 'البرامج' : 'Programs', value: stats.total_programs, icon: ClipboardList, color: '#22c55e', testId: 'f606-stat-programs' },
              { label: isAr ? 'الاشتراكات النشطة' : 'Active subs', value: stats.active_subscriptions, icon: CreditCard, color: '#06b6d4', testId: 'f606-stat-subs' },
            ].map((s) => (
              <div
                key={s.testId}
                className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-4"
                data-testid={s.testId}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-[#71717A] font-bold">{s.label}</span>
                  <s.icon className="h-4 w-4" style={{ color: s.color }} />
                </div>
                <div className="text-2xl font-black font-mono" style={{ color: s.color }} dir="ltr">
                  {(s.value ?? 0).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Operations center + sections grid */}
        <div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center mb-8"
          >
            <h3 className="text-xl font-bold text-[#F5F5F5]">
              {isAr ? 'اختر القسم' : 'Select Section'}
            </h3>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {/* Operations center card — write actions hub */}
            {canViewRoute('/control/f606/actions') && (
            <SectionCard
              icon={SlidersHorizontal}
              color="#10b981"
              name={isAr ? 'مركز العمليات' : 'Operations Center'}
              subtitle={isAr ? 'تأكيد وشحن الطلبات، الحظر، الهدايا، البثّ' : 'Confirm/ship orders, ban, gift, broadcast'}
              code="F606-OPS"
              onClick={() => navigate('/control/f606/actions')}
              testId="f606-section-actions"
            />
            )}
            {F606_SECTIONS.filter((s) => canViewRoute(`/control/f606/${s.id}`)).map((s) => (
              <SectionCard
                key={s.id}
                icon={s.icon}
                color={s.color}
                name={isAr ? s.ar : s.en}
                code={s.code}
                statVal={s.stat && stats ? stats[s.stat] : null}
                onClick={() => navigate(`/control/f606/${s.id}`)}
                testId={`f606-section-${s.id}`}
              />
            ))}
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}
